import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import ProductDetailModal from "../components/ProductDetailModal";
import EventDetailModal from "../components/EventDetailModal";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useAuthStore } from "../store/authStore";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";
import { buildChangeParams } from "../lib/changeParams";
import { type HitoCatalog } from "../lib/hitos";
import { HitoBadge, ImpactFlags, MonitoringNote, SeverityChip } from "../components/HitoBadge";
import SearchInput from "../components/SearchInput";

type Mark = {
  starred: boolean; reviewed: boolean; note: string | null; candidata?: "confirmada" | "descartada" | null;
  updated_by: string | null; updated_at: string | null;
} | null;

type Change = {
  id: string; change_type: string; cn_code: string; product_name: string;
  active_ingredient: string; old_value: string; new_value: string;
  detected_at: string; is_portfolio: boolean; is_competitor: boolean; relevance_score: number | null;
  laboratory: string | null;
  current_deduction: string | null;
  current_price: string | null;
  extra_data: { url?: string; asunto?: string; referencia?: string; fecha?: string; resuelto?: string; candidata?: string } | null;
  source_code: string | null; source_name: string | null;
  // Hito (§89): qué ha pasado, en el vocabulario del catálogo. null = sin mapear.
  hito_id: string | null; hito_name: string | null;
  hito_group: number | null; hito_group_name: string | null;
  severity: "A" | "D" | "I" | null;
  sentido: "favorable" | "adverso" | "neutro" | null;      // R-19: por hito × titularidad
  first_competitor?: boolean; above_precio_menor?: boolean | null; historico?: boolean;
  // Consolidación C-14 (§90 Fase 1C): cuántas fuentes confirman el mismo hecho.
  hito_event_id: string | null; confirmations: number; confirmed_by: string[];
  mark: Mark;
  meaning?: string | null;   // «Por qué importa» (catálogo 4.2), el mismo texto que el correo
  // Lo que cambia en el evento, el mismo texto que el aviso (Carlos, 20 oct 2026; app/eventos.py)
  detalle?: string | null; titulo?: string | null; explicacion?: string | null;
  antes?: string | null; despues?: string | null; source_url?: string | null;
};
type Page = { total: number; page: number; limit: number; items: Change[]; monitoring_since?: string | null };

/** Coincidencia candidata (catálogo 4.0: SG-02 y AP-11): la fuente no identifica el producto.
 *  Sin correo; el equipo la confirma (pasa a evento normal) o la descarta (sale de la bandeja). */
function CandidateChip({ change, canEdit }: { change: Change; canEdit: boolean }) {
  const t = useT();
  const [decision, setDecision] = useState(change.mark?.candidata ?? null);
  const [saving, setSaving] = useState(false);
  async function decide(value: "confirmada" | "descartada" | "") {
    if (!canEdit || saving) return;
    setSaving(true);
    try {
      const r = await api.put<{ mark: Mark }>(`/changes/${change.id}/mark`, { candidata: value });
      setDecision(r.mark?.candidata ?? null);
    } catch { /* sin permisos o error puntual: no romper la fila */ }
    finally { setSaving(false); }
  }
  const color = decision === "confirmada" ? "#15803d" : decision === "descartada" ? "#64748b" : "#b45309";
  const chip: React.CSSProperties = { display:"inline-block", marginLeft:8, fontSize:"0.68rem", fontWeight:600, color,
    background:`${color}14`, border:`1px solid ${color}33`, borderRadius:999, padding:"1px 8px", whiteSpace:"nowrap" };
  const link: React.CSSProperties = { background:"transparent", border:"none", padding:"0 4px", cursor:"pointer",
    fontSize:"0.68rem", fontWeight:600, color:"var(--clr-accent, #2563eb)" };
  const label = decision === "confirmada" ? t("changes.candidateConfirmed")
    : decision === "descartada" ? t("changes.candidateDiscarded") : t("changes.candidate");
  return (
    <span style={{ whiteSpace:"nowrap" }} onClick={e => e.stopPropagation()}>
      <span style={chip} title={change.extra_data?.candidata}>{label}</span>
      {canEdit && !decision && (<>
        <button style={link} disabled={saving} onClick={() => decide("confirmada")}>{t("changes.confirm")}</button>
        <button style={{ ...link, color:"var(--clr-text4)" }} disabled={saving} onClick={() => decide("descartada")}>
          {t("changes.discard")}</button>
      </>)}
      {canEdit && decision && (
        <button style={{ ...link, color:"var(--clr-text4)" }} disabled={saving} onClick={() => decide("")}>{t("changes.undo")}</button>
      )}
    </span>
  );
}


/** Celda de marcas de equipo: ⭐ destacar · ✓ revisado · 📝 nota compartida. */
function MarkCell({ change, canEdit }: { change: Change; canEdit: boolean }) {
  const t = useT();
  const [mark, setMark] = useState<Mark>(change.mark);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(change.mark?.note ?? "");
  const [saving, setSaving] = useState(false);

  async function update(body: Record<string, unknown>) {
    if (!canEdit || saving) return;
    setSaving(true);
    try {
      const r = await api.put<{ mark: Mark }>(`/changes/${change.id}/mark`, body);
      setMark(r.mark);
      if (body.note !== undefined) setNoteOpen(false);
    } catch { /* sin permisos o error puntual: no romper la fila */ }
    finally { setSaving(false); }
  }

  const btn = (active: boolean, color: string): React.CSSProperties => ({
    background: "transparent", border: "none", cursor: canEdit ? "pointer" : "default",
    lineHeight: 0, padding: "3px 3px", borderRadius: 6,
    opacity: active ? 1 : 0.3, color: active ? color : "var(--clr-text4)",
    display: "inline-flex", alignItems: "center", transition: "opacity 0.15s, color 0.15s",
  });
  const ico = { width: 15, height: 15, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
      <button title={mark?.starred ? t("changes.unstar") : t("changes.star")} disabled={!canEdit}
              onClick={() => update({ starred: !mark?.starred })}
              style={btn(!!mark?.starred, "#f59e0b")}>
        <svg {...ico} fill={mark?.starred ? "currentColor" : "none"}><path d="m12 3 2.6 5.3 5.9.9-4.2 4.1 1 5.8L12 16.9 6.7 19.6l1-5.8-4.2-4.1 5.9-.9L12 3Z" /></svg>
      </button>
      <button title={mark?.reviewed ? t("changes.reviewedBy", { who: mark.updated_by ?? t("changes.theTeam") }) : t("changes.markReviewed")}
              disabled={!canEdit}
              onClick={() => update({ reviewed: !mark?.reviewed })}
              style={btn(!!mark?.reviewed, "#22c55e")}>
        <svg {...ico}><path d="M20 6 9 17l-5-5" /></svg>
      </button>
      <button title={mark?.note ? t("changes.noteFrom", { who: mark.updated_by ?? t("changes.team"), note: mark.note }) : t("changes.addTeamNote")}
              disabled={!canEdit && !mark?.note}
              onClick={() => { setNoteDraft(mark?.note ?? ""); setNoteOpen(o => !o); }}
              style={btn(!!mark?.note, "#06b6d4")}>
        <svg {...ico}><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" /></svg>
      </button>

      {noteOpen && (
        <span style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 300,
          background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)",
          borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.25)",
          padding: 10, width: 240, display: "flex", flexDirection: "column", gap: 8,
        }}>
          <textarea
            value={noteDraft}
            onChange={e => setNoteDraft(e.target.value)}
            placeholder={t("changes.notePlaceholder")}
            readOnly={!canEdit}
            rows={3}
            style={{
              resize: "vertical", fontSize: "0.78rem", fontFamily: "inherit",
              background: "var(--clr-input-bg)", color: "var(--clr-text)",
              border: "1px solid var(--clr-border)", borderRadius: 7, padding: 7,
            }}
          />
          {mark?.note && mark.updated_by && (
            <span style={{ fontSize: "0.66rem", color: "var(--clr-text4)" }}>
              {t("changes.lastEdit", { who: mark.updated_by })}
            </span>
          )}
          <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button onClick={() => setNoteOpen(false)} style={{
              background: "transparent", border: "1px solid var(--clr-border)", borderRadius: 7,
              padding: "5px 10px", fontSize: "0.74rem", color: "var(--clr-text3)", cursor: "pointer",
            }}>{t("changes.close")}</button>
            {canEdit && (
              <button onClick={() => update({ note: noteDraft })} disabled={saving} style={{
                background: "var(--clr-org-primary)", border: 0, borderRadius: 999,
                padding: "5px 14px", fontSize: "0.74rem", color: "#fff", fontWeight: 600, cursor: "pointer",
              }}>{saving ? "…" : t("changes.save")}</button>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

function Badge({ type, typeColors, typeLabels }: { type: string; typeColors: Record<string, string>; typeLabels: Record<string, string> }) {
  const c = typeColors[type] ?? "#94a3b8";
  return <span style={{ background: c+"1c", color: c, padding:"2px 9px", borderRadius:999, fontSize:"0.72rem", fontWeight:600, whiteSpace:"nowrap" }}>{typeLabels[type]??type}</span>;
}

// Etiqueta corta por fuente: los nombres oficiales son largos («SNS – Precios
// menores AH») y no caben en una columna. El nombre completo va en
// el tooltip. Fallback: el `source_name` que manda la API.
const SOURCE_SHORT: Record<string, string> = {
  cima: "CIMA",
  boe: "BOE",
  cipm: "CIPM",
  ipt: "IPT",
  ema: "EMA",
  cgcof: "CGCOF",
  codem_cmh: "CMH",
  aemps_alerts: "Notas de seguridad",
  nomenclator_facturacion: "Nomenclátor",
  sns_deducciones: "Deducciones",
  sns_pmah: "PMAH",
  snsfarmacia: "Genéricos SNS",
  sprah_bajadas: "SPRAH bajadas",
  sprah_nuevas_financiaciones: "SPRAH financiación",
  // Catálogo 3.9 (28 sep 2026)
  sprah_nf_anual: "SPRAH anual (precios ref.)",
  calculo_interno: "Protección (cálculo)",
  bifimed: "BIFIMED",
  cima_implementacion: "CIMA implementación",
  registro_ue: "Registro UE",
  ema_evaluacion: "EMA en evaluación",
  ema_eurd: "EMA EURD",
  prac_senales: "Señales PRAC",
  aemps_estrategicos: "AEMPS estratégicos",
  ema_criticos: "Críticos UE",
  cipm_informes: "Informes CIPM",
  hta_jca: "JCA (UE)",
  valtermed: "VALTERMED",
  labofar_notificacion: "LABOFAR",
  sprah_proyecto_opr: "Proyecto OPR",
  cima_psuministro: "CIMA suministro",
  cima_cambios: "CIMA fichas",
  aemps_calidad: "AEMPS calidad",
  aemps_notas_suministro: "AEMPS suministro",
  aemps_notas_seguridad: "AEMPS seguridad",
  aemps_ipt: "AEMPS IPT",
  sns_nomenclator: "Nomenclátor facturación",
  ema_tablas: "EMA (tablas)",
};
// Las 8 fuentes que la lista oculta por defecto (tienen su propia sección).
const RADAR_SOURCES = new Set([
  "sprah_bajadas", "sprah_nuevas_financiaciones", "cipm",
  "ema", "codem_cmh", "ipt", "boe", "nomenclator_facturacion",
]);

export default function ChangesPage() {
  const t = useT();
  const fmt = useFmt();
  // Color de marca de la org (como en Dashboard) — teal solo como fallback
  const BRAND = useAuthStore(s => s.user?.primary_color) || "#06b6d4";
  const isSuperAdmin = useAuthStore(s => s.user?.role) === "superadmin";
  const orgName = useAuthStore(s => s.user?.organization_name) || "ITF";
  const TYPE_LABELS: Record<string, string> = {
    new_entry: t("changes.typeNewEntry"), price_change: t("changes.typePriceChange"),
    reimbursement_change: t("changes.typeReimbursementChange"), exclusion: t("changes.typeExclusion"),
    deduction: t("changes.typeDeduction"), warning: t("changes.typeWarning"),
    marketing_status: t("changes.typeMarketingStatus"),
    eu_registration: t("changes.typeEuRegistration"),
    exclusivity: t("changes.typeExclusivity"),
  };
  // La EMA publica el estado en inglés y lo guardamos crudo en `new_value`.
  const EMA_STATUS: Record<string, string> = {
    "Authorised": t("changes.emaAuthorised"),
    "Application withdrawn": t("changes.emaApplicationWithdrawn"),
    "Withdrawn": t("changes.emaWithdrawn"),
    "Refused": t("changes.emaRefused"),
  };
  // Color por TIPO de cambio (decisión de Luis, §89): ayuda a escanear la lista.
  // Lo que R-18 prohíbe es el semáforo de SEVERIDAD (rojo = urgente ≠ malo);
  // la columna «Prio.» con puntos rojos ya no existe. Estos tonos identifican
  // el tipo, no juzgan la noticia.
  const TYPE_COLORS: Record<string, string> = {
    new_entry:        "#3b82f6",  // azul    — nueva entrada
    price_change:     "#f59e0b",  // ámbar   — cambio de precio
    reimbursement_change: "#10b981", // esmeralda — financiación
    exclusion:        "#ef4444",  // rojo    — exclusión
    deduction:        "#8b5cf6",  // violeta — deducción
    warning:          "#f97316",  // naranja — aviso de seguridad
    marketing_status: "#ec4899",  // rosa    — entra/sale del mercado
    eu_registration:  "#0ea5e9",  // celeste — registro europeo EMA
    exclusivity:      "#a855f7",  // púrpura — protección y exclusividad (PE-02/PE-03)
  };
  const location = useLocation();
  const [data, setData] = useState<Page | null>(null);
  const [loading, setLoading] = useState(false);
  const { isMobile } = useBreakpoint();
  const [page, setPage] = useState(1);
  const [detailCn, setDetailCn] = useState<string | null>(null);
  const [openEvent, setOpenEvent] = useState<Change | null>(null);
  const [msg, setMsg] = useState("");   // errores de exportación: nunca fallar en silencio
  const [filters, setFilters] = useState(() => {
    const p = new URLSearchParams(location.search);
    // Backward compat: convertir is_portfolio/is_competitor de URL a tag unificado
    let tag = "";
    if (p.get("is_portfolio") === "true") tag = "itf";
    else if (p.get("is_competitor") === "true") tag = "competidor";
    return {
      change_type: "",
      tag,                            // "" | "itf" | "competidor" | "otros"
      search: p.get("search") ?? "",
      priority: "",
      date: p.get("date") ?? "",
      from_date: p.get("from_date") ?? "",
      to_date: p.get("to_date") ?? "",
      label: p.get("label") ?? "",   // descripción visible del filtro activo
      laboratory: p.get("laboratory") ?? "",  // filtro exacto por laboratorio
      marked: "",                    // "" | "starred" | "reviewed" | "pending" | "noted"
      // Filtros por hito (R-11, §89): grupo, hito concreto y severidad.
      hito_group: p.get("hito_group") ?? "",
      hito_id: p.get("hito_id") ?? "",
      severity: p.get("severity") ?? "",
      source: p.get("source") ?? "",   // filtro por fuente (Anexo II-A 4.4)
      atc: p.get("atc") ?? "",         // prefijo ATC (barras del Dashboard, Carlos 20 oct 2026)
      // C-14: por defecto una fila por hecho; el usuario puede ver cada detección.
      consolidated: (() => { try { return localStorage.getItem("changes.consolidated") !== "0"; } catch { return true; } })(),
    };
  });
  const role = useAuthStore(s => s.user?.role);
  const canMark = role === "admin" || role === "analyst";

  // Columnas de la tabla en UNA lista: el colSpan del estado vacío sale de aquí
  // (antes eran cuatro números a mano que se desincronizaban al añadir columnas).
  // «Hito» sustituye a «Tipo» como cabecera: la celda muestra el hito del
  // catálogo (§89) y cae al tipo de cambio si la fila no está mapeada.
  // Sin columna «Grupo»: repetía la información del color del hito y ocupaba
  // ancho (feedback Luis, 9 sep). El grupo sigue estando en el filtro.
  const headers: string[] = [
    ...(isSuperAdmin ? [] : [t("changes.colMarks")]),
    t("changes.colDate"),
    t("hitos.colHito"),
    // La severidad vuelve a la tabla (Carlos, 28 sep: «una columna que no informa
    // porque los datos están mal no se arregla escondiéndola»). El 89 % de
    // «Acción» venía de contar la historia recargada y todo el mercado; ahora es
    // hito × relación con tu cartera, editable por tu organización.
    ...(isMobile ? [] : [t("changes.colType"), t("hitos.colSeverity")]),
    // Carlos (20 oct 2026): fuera «precio actual / anterior / deducción», que solo servían en
    // unos pocos hitos; «Detalle» dice lo que cambia en cada evento. PVL y deducción vigentes,
    // en la ficha del producto.
    t("changes.colProduct"), t("events.colDetail"), t("changes.colActiveIngredient"),
    ...(isMobile ? [] : [t("changes.colCn")]),
    ...(isSuperAdmin ? [] : [t("changes.colLabel")]),
    ...(isMobile ? [] : [t("changes.colLaboratory")]),
    // La fuente cierra la tabla: es contexto, no lo primero que se mira (Luis, 9 sep).
    ...(isSuperAdmin && !isMobile ? [t("changes.colSource")] : []),
  ];

  // Catálogo de hitos (67, 11 grupos): alimenta los filtros y las etiquetas.
  const [catalog, setCatalog] = useState<HitoCatalog | null>(null);
  useEffect(() => { api.get<HitoCatalog>("/hitos").then(setCatalog).catch(() => {}); }, []);

  // Los mismos filtros para la lista y para el Excel. Antes el botón sólo mandaba
  // `change_type`: el fichero contenía algo distinto de lo que estabas viendo (§77).
  // La construcción de params vive en lib/changeParams.ts (testeable). El `label`
  // es solo UI (chip del filtro activo), no viaja al backend.
  const buildParams = useCallback(
    () => buildChangeParams(filters, isSuperAdmin),
    [filters, isSuperAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      params.set("page", String(page));
      params.set("limit", "50");
      setData(await api.get<Page>(`/changes?${params}`));
    } finally { setLoading(false); }
  }, [page, filters, isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const [exporting, setExporting] = useState(false);
  // Anexo II-A 4.8: exportación en XLSX y en CSV (el CSV existía en la API pero
  // no tenía botón).
  const exportChanges = async (format: "xlsx" | "csv") => {
    // `api.download` manda el token: una navegación `a.href` daba 401 (§77).
    setExporting(true);
    try {
      const params = buildParams();
      params.set("format", format);
      await api.download(`/export/changes?${params}`, `helixscan_eventos.${format}`);
    } catch (e: any) {
      setMsg(e?.message ?? "No se pudo exportar");
      setTimeout(() => setMsg(""), 4000);
    } finally { setExporting(false); }
  };

  const inp = (style?: React.CSSProperties): React.CSSProperties => ({
    background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)",
    borderRadius: 12, color: "var(--clr-text)", padding: "6px 12px", fontSize: "0.82rem",
    outline: "none", ...style,
  });

  return (
    <div style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:10 }}>
        <div>
          <h1 style={{ margin:0, fontSize:"1.4rem", fontWeight:700, letterSpacing:"-0.02em" }}>{t("changes.title")}</h1>
          <p style={{ margin:"4px 0 0 0", fontSize:"0.82rem", color:"var(--clr-text3)" }}>
            {t("changes.subtitle")}{" "}
            <span style={{ color:"var(--clr-text4)" }}>{t("changes.regulatoryPrefix")}</span>
            <a href="/radar" style={{ color:"var(--clr-org-primary)", textDecoration:"none", fontWeight:600 }}>{t("changes.regulatoryRadar")}</a>
          </p>
          <div style={{ marginTop:6 }}>
            <MonitoringNote since={data?.monitoring_since} t={t} fmtDate={d => fmt.date(d)} />
          </div>
        </div>
        {msg && <span style={{ color:"#f87171", fontSize:"0.8rem", alignSelf:"center" }}>{msg}</span>}
        <div style={{ display:"inline-flex", gap:8, alignItems:"center" }}>
          <button onClick={() => exportChanges("xlsx")} disabled={exporting} style={{ background:"var(--clr-org-primary)", color:"#fff", border:0, borderRadius:999, padding:"7px 18px", fontSize:"0.82rem", fontWeight:600, cursor:exporting?"default":"pointer", opacity:exporting?0.6:1, display:"inline-flex", alignItems:"center", gap:7 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></svg>
            {exporting ? t("changes.exporting") : t("changes.exportExcel")}
          </button>
          <button onClick={() => exportChanges("csv")} disabled={exporting} style={{ background:"transparent", color:"var(--clr-org-primary)", border:"1px solid var(--clr-org-primary)", borderRadius:999, padding:"6px 14px", fontSize:"0.82rem", fontWeight:600, cursor:exporting?"default":"pointer", opacity:exporting?0.6:1 }}>
            {t("changes.exportCsv")}
          </button>
        </div>
      </div>

      {/* Active filter badges */}
      {(filters.date || filters.label || filters.laboratory) && (
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12, flexWrap:"wrap" }}>
          {filters.laboratory && (
            <span style={{ background:"var(--clr-surface)", color:"var(--clr-text3)", border:"1px solid var(--clr-border)", padding:"4px 12px", borderRadius:999, fontSize:"0.78rem", fontWeight:500, display:"inline-flex", alignItems:"center", gap:6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M4 21V10l6 3V10l6 3V6a2 2 0 0 1 4 0v15" /></svg>
              {filters.laboratory}
            </span>
          )}
          {(filters.date || filters.label) && (
            <span style={{ background:"var(--clr-surface)", color:"var(--clr-text3)", border:"1px solid var(--clr-border)", padding:"4px 12px", borderRadius:999, fontSize:"0.78rem", fontWeight:500, display:"inline-flex", alignItems:"center", gap:6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                {filters.label
                  ? <path d="M3 3v18h18M8 15v3M13 10v8M18 6v12" />
                  : <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>}
              </svg>
              {filters.label
                ? filters.label
                : fmt.date(new Date(filters.date + "T12:00:00"), { weekday:"long", day:"numeric", month:"long" })}
            </span>
          )}
          <button onClick={() => { setFilters(f => ({ ...f, date:"", from_date:"", to_date:"", tag:"", label:"", laboratory:"", hito_group:"", hito_id:"", severity:"", source:"", atc:"" })); setPage(1); }}
            style={{ background:"transparent", border:"none", color:"var(--clr-text4)", cursor:"pointer", fontSize:"0.8rem", padding:"2px 6px", display:"inline-flex", alignItems:"center", gap:5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            {t("changes.clearFilter")}
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display:"flex", gap:10, marginBottom:18, flexWrap:"wrap" }}>
        <SearchInput value={filters.search}
          onChange={v => { setFilters(f=>({...f, search: v, laboratory: ""})); setPage(1); }}
          placeholder={t("changes.searchPlaceholder")} />
        <select value={filters.change_type} onChange={e => { setFilters(f=>({...f,change_type:e.target.value})); setPage(1); }} style={inp()}>
          <option value="">{t("changes.allTypes")}</option>
          {Object.entries(TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {/* Filtro por fuente (Anexo II-A 4.4). CGCOF está apagada: fuera. */}
        <select value={filters.source} onChange={e => { setFilters(f=>({...f, source:e.target.value})); setPage(1); }} style={inp()}>
          <option value="">{t("changes.allSources")}</option>
          {Object.entries(SOURCE_SHORT).filter(([k]) => k !== "cgcof")
            .map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {/* Filtros por HITO (R-11, §89): grupo → hito (dependiente) → severidad. */}
        <select value={filters.hito_group} style={inp()}
          onChange={e => { setFilters(f=>({...f, hito_group:e.target.value, hito_id:""})); setPage(1); }}>
          <option value="">{t("hitos.allGroups")}</option>
          {(catalog?.groups ?? []).map(g => <option key={g.grupo} value={g.grupo}>{t(`hitos.group${g.grupo}`)}</option>)}
        </select>
        <select value={filters.hito_id} style={{ ...inp(), maxWidth:280 }}
          onChange={e => { setFilters(f=>({...f, hito_id:e.target.value})); setPage(1); }}>
          <option value="">{t("hitos.allHitos")}</option>
          {(catalog?.items ?? [])
            .filter(h => !filters.hito_group || String(h.grupo) === filters.hito_group)
            .map(h => <option key={h.id} value={h.id}>
              {isSuperAdmin ? `${h.id} · ` : ""}{h.nombre}{h.cubierto === false ? ` (${t(h.activo === false ? "hitos.inactivo" : "hitos.sinCobertura")})` : ""}
            </option>)}
        </select>
        <select value={filters.severity} style={inp()}
          onChange={e => { setFilters(f=>({...f, severity:e.target.value})); setPage(1); }}>
          <option value="">{t("hitos.allSeverities")}</option>
          <option value="A">{t("hitos.sevA")}</option>
          <option value="D">{t("hitos.sevD")}</option>
          <option value="I">{t("hitos.sevI")}</option>
        </select>
        {/* C-14 (§90 Fase 1C): una fila por hecho, con sus confirmaciones dentro */}
        <label title={t("changes.consolidateHint")} style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:"0.8rem", color:"var(--clr-text3)", cursor:"pointer", padding:"0 4px" }}>
          <input type="checkbox" checked={filters.consolidated}
            onChange={e => { const v = e.target.checked; try { localStorage.setItem("changes.consolidated", v ? "1" : "0"); } catch { /* sin storage */ } setFilters(f=>({...f, consolidated: v})); setPage(1); }} />
          {t("changes.consolidate")}
        </label>
        {!isSuperAdmin && (
          <select value={filters.tag} onChange={e => { setFilters(f=>({...f,tag:e.target.value})); setPage(1); }} style={inp()}>
            <option value="">{t("changes.myLabAndCompetition")}</option>
            <option value="itf">{t("changes.onlyOrg", { org: orgName })}</option>
            <option value="competidor">{t("changes.onlyCompetition")}</option>
            <option value="mercado">{t("changes.allMarket")}</option>
          </select>
        )}
        {/* R-17 (§89): el filtro «Prioridad» (alta/media/baja) era la titularidad
            con otro nombre — duplicaba la pestaña Portfolio/Competencia/Mercado. */}
        {!isSuperAdmin && (
          <select value={filters.marked} onChange={e => { setFilters(f=>({...f,marked:e.target.value})); setPage(1); }} style={inp()}>
            <option value="">{t("changes.allMarks")}</option>
            <option value="starred">{t("changes.starred")}</option>
            <option value="pending">{t("changes.pendingReview")}</option>
            <option value="reviewed">{t("changes.reviewed")}</option>
            <option value="noted">{t("changes.withNote")}</option>
            <option value="candidates">{t("changes.pendingCandidates")}</option>
            <option value="discarded">{t("changes.discardedCandidates")}</option>
          </select>
        )}
      </div>

      {/* Table */}
      <div style={{ background:"var(--clr-surface)", border:"1px solid var(--clr-border)", borderRadius:18, overflow:"hidden", backdropFilter:"var(--clr-blur)", WebkitBackdropFilter:"var(--clr-blur)", boxShadow:"var(--clr-shadow-sm)" }}>
        {loading && <div style={{ padding:16, textAlign:"center", color:"var(--clr-text4)", fontSize:"0.82rem" }}>{t("changes.loading")}</div>}
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.82rem" }}>
            <thead>
              <tr style={{ textAlign:"left", borderBottom:"1px solid var(--clr-border2)" }}>
                {headers.map(h =>
                  <th key={h} style={{ padding:"0.7rem 12px", fontSize:"0.65rem", fontWeight:600, textTransform:"uppercase", letterSpacing:0.9, color:"var(--clr-text4)", whiteSpace:"nowrap" }}>{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {(!data || data.items.length===0) && !loading && (
                <tr><td colSpan={headers.length} style={{ padding:32, textAlign:"center", color:"var(--clr-text4)" }}>{t("changes.noResults")}</td></tr>
              )}
              {data?.items.map(c => (
                <tr key={c.id} style={{ borderTop:"1px solid var(--clr-border2)" }}>
                  {!isSuperAdmin && (
                    <td style={{ padding:"6px 8px" }}>
                      <MarkCell change={c} canEdit={canMark} />
                    </td>
                  )}
                  <td style={{ padding:"8px 12px", color:"var(--clr-text3)", fontSize:"0.75rem", whiteSpace:"nowrap" }}>{c.detected_at?fmt.date(new Date(c.detected_at), { timeZone: "UTC" }):"–"}</td>
                  {/* R-17: la columna «Prio.» era la titularidad disfrazada; la
                      titularidad ya tiene su propia columna (chips Portfolio /
                      Competidor / Otros) más abajo. */}
                  <td style={{ padding:"8px 12px" }}>
                    {/* Solo el código: el tipo y la severidad tienen columna
                        propia desde el 9 sep (orden pedido por Luis). */}
                    <HitoBadge c={c} t={t} showCode showSeverity={false} maxWidth={isSuperAdmin ? 160 : 220}
                      title={`${isSuperAdmin ? `${c.hito_id} · ` : ""}${c.hito_name ?? ""} · ${TYPE_LABELS[c.change_type] ?? c.change_type}`}
                      fallback={<span style={{ color:"var(--clr-text4)", fontSize:"0.72rem" }}>–</span>} />
                    <ImpactFlags c={c} t={t} />
                    {c.confirmations > 1 && (
                      /* C-14: el mismo hecho confirmado por varias fuentes */
                      <span title={t("changes.confirmedBy", { n: c.confirmations, sources: c.confirmed_by.map(s => SOURCE_SHORT[s] ?? s).join(", ") })}
                        style={{ marginLeft: 6, border:"1px solid var(--clr-border)", color:"var(--clr-text3)", borderRadius:999, padding:"1px 7px", fontSize:"0.65rem", fontWeight:700, whiteSpace:"nowrap" }}>
                        ×{c.confirmations}
                      </span>
                    )}
                  </td>
                  {!isMobile && (
                    <td style={{ padding:"8px 12px" }}>
                      <Badge type={c.change_type} typeColors={TYPE_COLORS} typeLabels={TYPE_LABELS} />
                    </td>
                  )}
                  {!isMobile && (
                    <td style={{ padding:"8px 12px" }}>
                      {c.severity ? <SeverityChip severity={c.severity} t={t} /> : <span style={{ color:"var(--clr-text4)", fontSize:"0.72rem" }}>–</span>}
                    </td>
                  )}
                  <td style={{ padding:"4px 8px", maxWidth:240 }}>
                    {c.change_type === "eu_registration" && !c.cn_code && c.extra_data?.url ? (
                      /* Registro europeo: no hay CN español (y por tanto no hay
                         ficha) hasta que se comercializa aquí. Enlazamos al EPAR
                         de la EMA y mostramos el estado REAL, que puede ser una
                         retirada o un rechazo, no sólo una autorización. */
                      <a
                        href={c.extra_data.url}
                        target="_blank" rel="noreferrer"
                        title={`${c.product_name} — ${EMA_STATUS[c.new_value ?? ""] ?? c.new_value ?? ""}`}
                        style={{
                          display:"flex", flexDirection:"column", gap:2, width:"100%",
                          background:"transparent", border:"1px solid transparent", borderRadius:6,
                          padding:"4px 8px", cursor:"pointer", textDecoration:"none",
                          transition:"background 0.15s ease, border-color 0.15s ease",
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLAnchorElement).style.background = "var(--clr-surface2, rgba(255,255,255,0.5))";
                          (e.currentTarget as HTMLAnchorElement).style.borderColor = "var(--clr-border)";
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                          (e.currentTarget as HTMLAnchorElement).style.borderColor = "transparent";
                        }}>
                        <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color:"var(--clr-text)", fontWeight:600, fontSize:"0.82rem" }}>
                            {c.product_name}
                          </span>
                          <span style={{ fontSize:"0.72rem", color:"var(--clr-text4)", flexShrink:0 }} aria-hidden>↗</span>
                        </span>
                        {c.new_value && (
                          <span style={{ fontSize:"0.68rem", color:"var(--clr-text4)", whiteSpace:"nowrap" }}>
                            {EMA_STATUS[c.new_value] ?? c.new_value}
                          </span>
                        )}
                      </a>
                    ) : c.change_type === "warning" && !c.cn_code ? (
                      /* Aviso AEMPS: enlace externo a la nota oficial */
                      <a
                        href={c.extra_data?.url || "#"}
                        target="_blank" rel="noreferrer"
                        title={c.new_value || c.product_name}
                        style={{
                          display:"flex", flexDirection:"column", gap:2, width:"100%",
                          background:"transparent", border:"1px solid transparent", borderRadius:6,
                          padding:"4px 8px", cursor:"pointer", textDecoration:"none",
                          transition:"background 0.15s ease, border-color 0.15s ease",
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLAnchorElement).style.background = "var(--clr-surface2, rgba(255,255,255,0.5))";
                          (e.currentTarget as HTMLAnchorElement).style.borderColor = "var(--clr-border)";
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                          (e.currentTarget as HTMLAnchorElement).style.borderColor = "transparent";
                        }}>
                        <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color:"var(--clr-text)", fontWeight:600, fontSize:"0.82rem" }}>
                            {c.new_value || c.product_name}
                          </span>
                          <span style={{ fontSize:"0.72rem", color:"var(--clr-text4)", flexShrink:0 }} aria-hidden>↗</span>
                        </span>
                        {c.product_name && (
                          <span style={{ fontSize:"0.68rem", color:"var(--clr-text4)", whiteSpace:"nowrap" }}>
                            {c.product_name}
                          </span>
                        )}
                      </a>
                    ) : c.cn_code && c.product_name ? (
                      <button onClick={() => setDetailCn(c.cn_code)}
                        title={t("changes.viewProduct", { name: c.product_name })}
                        className="product-link"
                        style={{
                          display:"flex", alignItems:"center", gap:6, width:"100%",
                          background:"transparent", border:"1px solid transparent", borderRadius:6,
                          padding:"4px 8px", cursor:"pointer",
                          color:c.is_portfolio?BRAND:"var(--clr-text)", fontSize:"inherit",
                          textAlign:"left", fontFamily:"inherit", fontWeight:c.is_portfolio?600:500,
                          transition:"background 0.15s ease, border-color 0.15s ease",
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = "var(--clr-surface2, rgba(255,255,255,0.5))";
                          e.currentTarget.style.borderColor = "var(--clr-border)";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "transparent";
                        }}>
                        <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {c.product_name}
                        </span>
                        <span style={{ fontSize:"0.78rem", color:"var(--clr-text4)", flexShrink:0 }} aria-hidden>↗</span>
                      </button>
                    ) : (
                      <span style={{ padding:"4px 8px", color:"var(--clr-text4)" }}>{c.product_name || "–"}</span>
                    )}
                    {/* R-31 (§90): el detalle debe llevar al documento que originó el evento (texto del BOE,
                        acuerdo CIPM, nota AEMPS…). La ficha CIMA solo es el destino correcto para los hitos
                        de registro (grupo 8) y SG-05; para el resto, si la fuente trae URL, se ofrece aquí. */}
                    {c.extra_data?.url && c.cn_code && c.change_type !== "eu_registration" && c.change_type !== "warning" && (
                      <a href={c.extra_data.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                         title={t("changes.originDoc")}
                         style={{ display:"inline-flex", alignItems:"center", gap:4, marginLeft:8, fontSize:"0.68rem", color:"var(--clr-text4)", textDecoration:"none", whiteSpace:"nowrap" }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
                        {t("changes.originDocShort")}
                      </a>
                    )}
                    {c.extra_data?.candidata && <CandidateChip change={c} canEdit={canMark} />}
                    {/* Catálogo 4.0: la resolución (CO-08) cierra el problema de suministro abierto. */}
                    {c.extra_data?.resuelto && (
                      <span style={{ display:"inline-block", marginLeft:8, fontSize:"0.68rem", fontWeight:600, color:"#15803d",
                                     background:"#15803d14", border:"1px solid #15803d33", borderRadius:999, padding:"1px 8px", whiteSpace:"nowrap" }}>
                        {t("changes.resolvedOn", { date: new Date(c.extra_data.resuelto).toLocaleDateString(undefined) })}
                      </span>
                    )}
                    {c.meaning && (
                      /* Catálogo 4.2: «Por qué importa» como línea secundaria bajo el evento. */
                      <div title={c.meaning}
                        style={{ marginTop:3, padding:"0 8px", fontSize:"0.7rem", lineHeight:1.35, color:"var(--clr-text4)",
                                 display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden",
                                 whiteSpace:"normal", minWidth:200 }}>
                        {c.meaning}
                      </div>
                    )}
                  </td>
                  {/* Detalle: lo que cambia en el evento; abre el evento completo */}
                  <td style={{ padding:"4px 8px", maxWidth:300, minWidth:160 }}>
                    <button onClick={() => setOpenEvent(c)} title={c.explicacion || c.detalle || t("events.openEvent")}
                      style={{ width:"100%", textAlign:"left", background:"transparent", border:"1px solid transparent",
                               borderRadius:6, padding:"4px 8px", cursor:"pointer", fontFamily:"inherit", fontSize:"0.78rem",
                               color: c.detalle ? "var(--clr-text)" : "var(--clr-text4)", lineHeight:1.35,
                               display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" } as React.CSSProperties}
                      onMouseEnter={e => { e.currentTarget.style.background = "var(--clr-surface2, rgba(255,255,255,0.5))"; e.currentTarget.style.borderColor = "var(--clr-border)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}>
                      {c.detalle || "–"}
                    </button>
                  </td>
                  {/* Tope de ancho: una combinación de tres principios activos
                      estiraba la columna para toda la tabla (Luis, 9 sep).
                      El valor completo queda en el tooltip. */}
                  <td title={c.active_ingredient || undefined}
                    style={{ padding:"8px 12px", color:"var(--clr-text2)", whiteSpace:"nowrap", textTransform:"uppercase",
                             maxWidth:200, overflow:"hidden", textOverflow:"ellipsis" }}>{c.active_ingredient||"–"}</td>
                  {!isMobile && <td style={{ padding:"8px 12px", color:"var(--clr-text3)", fontSize:"0.75rem" }}>{c.cn_code||"–"}</td>}
                  {!isSuperAdmin && (
                    <td style={{ padding:"8px 12px" }}>
                      {c.is_portfolio&&<span style={{ background:BRAND+"1e",color:BRAND,padding:"3px 10px",borderRadius:999,fontSize:"0.68rem",fontWeight:600,marginRight:4,whiteSpace:"nowrap" }}>{orgName}</span>}
                      {c.is_competitor&&<span style={{ background:"#f59e0b1e",color:"#f59e0b",padding:"3px 10px",borderRadius:999,fontSize:"0.68rem",fontWeight:600,whiteSpace:"nowrap" }}>{t("changes.competitor")}</span>}
                      {!c.is_portfolio&&!c.is_competitor&&<span style={{ background:"#94a3b81e",color:"#94a3b8",padding:"3px 10px",borderRadius:999,fontSize:"0.68rem",fontWeight:600,whiteSpace:"nowrap" }}>{t("changes.others")}</span>}
                    </td>
                  )}
                  {!isMobile && <td style={{ padding:"8px 12px", fontSize:"0.78rem", whiteSpace:"nowrap" }}>
                    {c.laboratory ? (
                      <button
                        onClick={() => { setFilters(f => ({ ...f, laboratory: c.laboratory!, search: "" })); setPage(1); }}
                        title={t("changes.viewAllChangesOf", { lab: c.laboratory })}
                        style={{
                          background: "transparent", border: "none", padding: "2px 7px",
                          borderRadius: 5, cursor: "pointer", color: BRAND,
                          fontSize: "0.78rem", fontWeight: 600, transition: "background 0.15s",
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLButtonElement).style.background = BRAND + "18";
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                        }}
                      >
                        {c.laboratory}
                        <span style={{ fontSize: "0.65rem", opacity: 0.65 }}>↗</span>
                      </button>
                    ) : <span style={{ color: "var(--clr-text4)" }}>–</span>}
                  </td>}
                  {isSuperAdmin && !isMobile && (
                    <td style={{ padding:"8px 12px", whiteSpace:"nowrap" }}>
                      <span
                        title={`${c.source_name ?? "–"}${c.source_code && RADAR_SOURCES.has(c.source_code) ? " · Radar Regulatorio" : ""}`}
                        style={{
                          fontSize:"0.72rem",
                          color: c.source_code && RADAR_SOURCES.has(c.source_code) ? "var(--clr-text4)" : "var(--clr-text3)",
                          fontStyle: c.source_code && RADAR_SOURCES.has(c.source_code) ? "italic" : "normal",
                        }}>
                        {c.source_code ? (SOURCE_SHORT[c.source_code] ?? c.source_name ?? c.source_code) : "–"}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.total > data.limit && (() => {
          const totalPages = Math.ceil(data.total / data.limit);
          const btnBase: React.CSSProperties = {
            minWidth: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center",
            border: "1px solid var(--clr-border)", borderRadius: 999, fontSize: "0.82rem",
            cursor: "pointer", fontWeight: 600, userSelect: "none", transition: "all 0.15s",
          };
          const pageBtn = (p: number) => (
            <button key={p} onClick={() => setPage(p)} style={{
              ...btnBase,
              background: page === p ? "var(--clr-org-primary)" : "var(--clr-surface)",
              color: page === p ? "#fff" : "var(--clr-text3)",
              borderColor: page === p ? "transparent" : "var(--clr-border)",
            }}>{p}</button>
          );
          const ellipsis = (k: string) => (
            <span key={k} style={{ minWidth: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--clr-text4)", fontSize: "0.82rem" }}>…</span>
          );
          const navBtn = (label: string, disabled: boolean, onClick: () => void) => (
            <button key={label} onClick={onClick} disabled={disabled} style={{
              ...btnBase,
              background: "var(--clr-surface)",
              color: disabled ? "var(--clr-text4)" : "var(--clr-text3)",
              opacity: disabled ? 0.4 : 1,
              cursor: disabled ? "not-allowed" : "pointer",
            }}>{label}</button>
          );

          // Build page number list with ellipsis
          const pages: React.ReactNode[] = [];
          const delta = 2;
          const left = page - delta;
          const right = page + delta;

          pages.push(navBtn("‹", page === 1, () => setPage(p => Math.max(1, p - 1))));
          pages.push(pageBtn(1));
          if (left > 2) pages.push(ellipsis("el"));
          for (let i = Math.max(2, left); i <= Math.min(totalPages - 1, right); i++) {
            pages.push(pageBtn(i));
          }
          if (right < totalPages - 1) pages.push(ellipsis("er"));
          if (totalPages > 1) pages.push(pageBtn(totalPages));
          pages.push(navBtn("›", page === totalPages, () => setPage(p => Math.min(totalPages, p + 1))));

          return (
            <div style={{ display: "flex", gap: 4, justifyContent: "center", alignItems: "center", padding: "12px 0", borderTop: "1px solid var(--clr-border2)" }}>
              {pages}
              <span style={{ marginLeft: 12, fontSize: "0.75rem", color: "var(--clr-text4)" }}>
                {t("changes.pageOf", { page, total: totalPages })}
              </span>
            </div>
          );
        })()}
      </div>
      {data && <div style={{ marginTop:10, fontSize:"0.75rem", color:"var(--clr-text4)" }}>{t("changes.recordsFound", { total: data.total })}</div>}

      {openEvent && <EventDetailModal event={openEvent} onClose={() => setOpenEvent(null)}
        onOpenProduct={cn => { setOpenEvent(null); setDetailCn(cn); }} />}
      {detailCn && <ProductDetailModal cn={detailCn} onClose={() => setDetailCn(null)} />}
    </div>
  );
}
